import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { 
  Users, Search, Filter, Download, Trash2, Eye, MapPin, Calendar, 
  Mail, Phone, Loader2, CheckCircle, Clock, IndianRupee
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminRegistrations = () => {
  const { getAuthAxios, fetchDashboard } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [registrations, setRegistrations] = useState([]);
  const [filteredRegistrations, setFilteredRegistrations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [eventFilter, setEventFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedReg, setSelectedReg] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  useEffect(() => {
    fetchRegistrations();
  }, []);

  useEffect(() => {
    filterRegistrations();
  }, [registrations, searchTerm, eventFilter, statusFilter]);

  const fetchRegistrations = async () => {
    try {
      const authAxios = getAuthAxios();
      const response = await authAxios.get(`${API}/admin/registrations`);
      setRegistrations(response.data.registrations || []);
    } catch (error) {
      toast.error("Failed to fetch registrations");
    } finally {
      setLoading(false);
    }
  };

  const filterRegistrations = () => {
    let items = [...registrations];
    
    if (eventFilter !== "all") {
      items = items.filter(r => r.event === eventFilter);
    }
    
    if (statusFilter !== "all") {
      items = items.filter(r => r.payment_status === statusFilter);
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      items = items.filter(r => 
        r.first_name?.toLowerCase().includes(term) ||
        r.last_name?.toLowerCase().includes(term) ||
        r.email?.toLowerCase().includes(term) ||
        r.phone?.includes(term) ||
        r.ticket_number?.toLowerCase().includes(term)
      );
    }
    
    setFilteredRegistrations(items);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this registration?")) return;
    
    try {
      const authAxios = getAuthAxios();
      await authAxios.delete(`${API}/admin/registrations/${id}`);
      toast.success("Registration deleted");
      fetchRegistrations();
      fetchDashboard();
    } catch (error) {
      toast.error("Failed to delete registration");
    }
  };

  const exportCSV = () => {
    const headers = ["Name", "Email", "Phone", "Event", "Category", "Status", "Amount", "Ticket Number", "T-Shirt Size"];
    const rows = filteredRegistrations.map(r => [
      `${r.first_name} ${r.last_name}`,
      r.email,
      r.phone,
      r.event === "chennai" ? "Chennai 2026" : "Hyderabad 2026",
      r.race_category,
      r.payment_status,
      r.amount / 100,
      r.ticket_number || "N/A",
      r.tshirt_size
    ]);
    
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `finrun_registrations_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
  };

  const paidCount = registrations.filter(r => r.payment_status === "completed").length;
  const pendingCount = registrations.filter(r => r.payment_status === "pending").length;
  const totalRevenue = registrations
    .filter(r => r.payment_status === "completed")
    .reduce((sum, r) => sum + (r.amount || 0), 0) / 100;

  return (
    <div data-testid="admin-registrations">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#0A0A0A] font-['Outfit']">Registrations</h1>
          <p className="text-[#4B5563]">Manage runner registrations</p>
        </div>
        
        <Button onClick={exportCSV} variant="outline" className="flex items-center gap-2">
          <Download className="w-4 h-4" />
          Export CSV
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{registrations.length}</p>
              <p className="text-xs text-[#4B5563]">Total</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{paidCount}</p>
              <p className="text-xs text-[#4B5563]">Paid</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{pendingCount}</p>
              <p className="text-xs text-[#4B5563]">Pending</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
              <IndianRupee className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">₹{totalRevenue.toLocaleString()}</p>
              <p className="text-xs text-[#4B5563]">Revenue</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input 
            placeholder="Search by name, email, phone, or ticket..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={eventFilter} onValueChange={setEventFilter}>
          <SelectTrigger className="w-full sm:w-48">
            <MapPin className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Event" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Events</SelectItem>
            <SelectItem value="chennai">Chennai 2026</SelectItem>
            <SelectItem value="hyderabad">Hyderabad 2026</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="completed">Paid</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#FF3B30]" />
        </div>
      ) : filteredRegistrations.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Registrations Found</h3>
            <p className="text-[#4B5563]">
              {searchTerm || eventFilter !== "all" || statusFilter !== "all"
                ? "Try adjusting your filters"
                : "No registrations yet"
              }
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#FAFAFA] border-b">
                  <tr>
                    <th className="text-left p-4 text-sm font-medium text-[#4B5563]">Participant</th>
                    <th className="text-left p-4 text-sm font-medium text-[#4B5563]">Event</th>
                    <th className="text-left p-4 text-sm font-medium text-[#4B5563]">Category</th>
                    <th className="text-left p-4 text-sm font-medium text-[#4B5563]">Status</th>
                    <th className="text-left p-4 text-sm font-medium text-[#4B5563]">Amount</th>
                    <th className="text-left p-4 text-sm font-medium text-[#4B5563]">Ticket</th>
                    <th className="text-left p-4 text-sm font-medium text-[#4B5563]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRegistrations.map((reg) => (
                    <tr key={reg.id} className="border-b last:border-0 hover:bg-gray-50">
                      <td className="p-4">
                        <p className="font-medium text-[#0A0A0A]">{reg.first_name} {reg.last_name}</p>
                        <p className="text-xs text-[#4B5563]">{reg.email}</p>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          reg.event === "chennai" 
                            ? "bg-red-100 text-red-700" 
                            : "bg-blue-100 text-blue-700"
                        }`}>
                          {reg.event === "chennai" ? "Chennai" : "Hyderabad"}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className="px-2 py-1 bg-gray-100 rounded-full text-xs font-medium">
                          {reg.race_category}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          reg.payment_status === "completed" 
                            ? "bg-green-100 text-green-700" 
                            : "bg-yellow-100 text-yellow-700"
                        }`}>
                          {reg.payment_status === "completed" ? "Paid" : "Pending"}
                        </span>
                      </td>
                      <td className="p-4 font-medium">₹{(reg.amount / 100).toLocaleString()}</td>
                      <td className="p-4 font-mono text-xs">{reg.ticket_number || "-"}</td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <button 
                            onClick={() => { setSelectedReg(reg); setViewDialogOpen(true); }}
                            className="p-2 hover:bg-gray-100 rounded-lg"
                          >
                            <Eye className="w-4 h-4 text-[#4B5563]" />
                          </button>
                          <button 
                            onClick={() => handleDelete(reg.id)}
                            className="p-2 hover:bg-red-50 rounded-lg"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Registration Details</DialogTitle>
          </DialogHeader>
          {selectedReg && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-[#4B5563]">Name</p>
                  <p className="font-medium">{selectedReg.first_name} {selectedReg.last_name}</p>
                </div>
                <div>
                  <p className="text-xs text-[#4B5563]">Gender</p>
                  <p className="font-medium capitalize">{selectedReg.gender}</p>
                </div>
                <div>
                  <p className="text-xs text-[#4B5563]">Email</p>
                  <p className="font-medium">{selectedReg.email}</p>
                </div>
                <div>
                  <p className="text-xs text-[#4B5563]">Phone</p>
                  <p className="font-medium">{selectedReg.phone}</p>
                </div>
                <div>
                  <p className="text-xs text-[#4B5563]">Age</p>
                  <p className="font-medium">{selectedReg.age} years</p>
                </div>
                <div>
                  <p className="text-xs text-[#4B5563]">T-Shirt Size</p>
                  <p className="font-medium">{selectedReg.tshirt_size}</p>
                </div>
                <div>
                  <p className="text-xs text-[#4B5563]">Event</p>
                  <p className="font-medium">{selectedReg.event === "chennai" ? "Chennai 2026" : "Hyderabad 2026"}</p>
                </div>
                <div>
                  <p className="text-xs text-[#4B5563]">Category</p>
                  <p className="font-medium">{selectedReg.race_category}</p>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <p className="text-xs text-[#4B5563] mb-2">Emergency Contact</p>
                <p className="font-medium">{selectedReg.emergency_contact_name}</p>
                <p className="text-sm text-[#4B5563]">{selectedReg.emergency_contact_phone}</p>
              </div>
              
              {selectedReg.blood_group && (
                <div>
                  <p className="text-xs text-[#4B5563]">Blood Group</p>
                  <p className="font-medium">{selectedReg.blood_group}</p>
                </div>
              )}
              
              {selectedReg.medical_conditions && (
                <div>
                  <p className="text-xs text-[#4B5563]">Medical Conditions</p>
                  <p className="font-medium">{selectedReg.medical_conditions}</p>
                </div>
              )}
              
              <div className="border-t pt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-[#4B5563]">Ticket Number</p>
                    <p className="font-mono font-medium">{selectedReg.ticket_number || "Not generated"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#4B5563]">Payment ID</p>
                    <p className="font-mono text-sm">{selectedReg.payment_id || "-"}</p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminRegistrations;
